import React, { useState, useEffect } from 'react';
import { FaEdit, FaTrash, FaPlus, FaSearch, FaSave, FaTimes } from 'react-icons/fa';
import Toggle from '../../../Common/Toggle/Toggle';
import '../Nucleos/Nucleos.css';

import API_BASE_URL from '../../../../config/api';

const Projetos = () => {
  const [projetos, setProjetos] = useState([]);
  const [filteredProjetos, setFilteredProjetos] = useState([]);
  const [prazosValidacao, setPrazosValidacao] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingProjeto, setEditingProjeto] = useState(null);
  const [formData, setFormData] = useState({
    projeto: '',
    id_prazo: '',
    ativo: true
  });

  const getPrazoLabel = (projeto) => {
    if (projeto?.prazo_descricao) {
      return projeto.prazo_descricao;
    }

    if (!projeto?.id_prazo) {
      return '';
    }

    const prazo = prazosValidacao.find((p) => p.id === projeto.id_prazo);
    if (!prazo) {
      return '';
    }

    return `${prazo.descricao} (${prazo.validade} dias)`;
  };

  useEffect(() => {
    const fetchProjetos = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/projetos`);

        if (!response.ok) {
          throw new Error('Erro ao buscar projetos');
        }

        const data = await response.json();

        setProjetos(data);
        setFilteredProjetos(data);
      } catch (error) {
        console.error('Erro ao carregar projetos:', error);
        alert('Erro ao carregar projetos. Verifique a conexão com o servidor.');
      }
    };

    const fetchPrazosValidacao = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/prazos-validacao`);

        if (!response.ok) {
          throw new Error('Erro ao buscar prazos de validação');
        }

        const data = await response.json();
        setPrazosValidacao(data);
      } catch (error) {
        console.error('Erro ao carregar prazos de validação:', error);
        setPrazosValidacao([]);
      }
    };

    fetchProjetos();
    fetchPrazosValidacao();
  }, []);

  useEffect(() => {
    const filtered = projetos.filter((projeto) =>
      `${projeto.projeto} ${projeto.prazo_descricao || ''}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
    );
    setFilteredProjetos(filtered);
  }, [searchTerm, projetos]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.projeto.trim()) {
      alert('A descrição do projeto é obrigatória.');
      return;
    }

    try {
      if (editingProjeto) {
        const response = await fetch(
          `${API_BASE_URL}/projetos/${editingProjeto.id}`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              projeto: formData.projeto,
              id_prazo: formData.id_prazo ? Number(formData.id_prazo) : null,
              ativo: formData.ativo
            })
          }
        );

        if (!response.ok) {
          throw new Error('Erro ao atualizar projeto');
        }

        const atualizado = await response.json();

        setProjetos((prev) =>
          prev.map((p) => (p.id === atualizado.id ? atualizado : p))
        );
      } else {
        const response = await fetch(`${API_BASE_URL}/projetos`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            projeto: formData.projeto,
            id_prazo: formData.id_prazo ? Number(formData.id_prazo) : null,
            ativo: formData.ativo
          })
        });

        if (!response.ok) {
          throw new Error('Erro ao criar projeto');
        }

        const criado = await response.json();

        setProjetos((prev) => [...prev, criado]);
      }

      resetForm();
    } catch (error) {
      console.error('Erro ao salvar projeto:', error);
      alert('Erro ao salvar projeto. Verifique os dados e tente novamente.');
    }
  };

  const handleEdit = (projeto) => {
    setEditingProjeto(projeto);
    setFormData({
      projeto: projeto.projeto,
      id_prazo: projeto.id_prazo ? String(projeto.id_prazo) : '',
      ativo: projeto.ativo
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este projeto?')) {
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/projetos/${id}`,
        {
          method: 'DELETE'
        }
      );

      if (!response.ok && response.status !== 204) {
        throw new Error('Erro ao excluir projeto');
      }

      setProjetos((prev) => prev.filter((projeto) => projeto.id !== id));
    } catch (error) {
      console.error('Erro ao excluir projeto:', error);
      alert('Erro ao excluir projeto. Tente novamente.');
    }
  };

  const resetForm = () => {
    setFormData({
      projeto: '',
      id_prazo: '',
      ativo: true
    });
    setEditingProjeto(null);
    setShowForm(false);
  };

  const handleToggleStatus = async (id) => {
    const selecionado = projetos.find((p) => p.id === id);

    if (!selecionado) {
      return;
    }

    const novoAtivo = !selecionado.ativo;

    try {
      const response = await fetch(
        `${API_BASE_URL}/projetos/${id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            projeto: selecionado.projeto,
            id_prazo: selecionado.id_prazo ?? null,
            ativo: novoAtivo
          })
        }
      );

      if (!response.ok) {
        throw new Error('Erro ao atualizar status do projeto');
      }

      const atualizado = await response.json();

      setProjetos((prev) =>
        prev.map((p) => (p.id === atualizado.id ? atualizado : p))
      );
    } catch (error) {
      console.error('Erro ao atualizar status do projeto:', error);
      alert('Erro ao atualizar status do projeto. Tente novamente.');
    }
  };

  return (
    <div className="nucleos-container">
      <div className="tabela-header">
        <div className="header-content">
          <h1>Projetos</h1>
        </div>
      </div>

      {showForm ? (
        <div className="form-container inline-form">
          <div className="form-header">
            <h2>{editingProjeto ? 'Editar Projeto' : 'Novo Projeto'}</h2>
          </div>
          
          <form onSubmit={handleSubmit} className="form-content">
            <div className="form-group">
              <label htmlFor="projeto">Projeto *</label>
              <textarea
                id="projeto"
                value={formData.projeto}
                onChange={(e) =>
                  setFormData({ ...formData, projeto: e.target.value })
                }
                required
                rows={4}
                maxLength={1000}
                placeholder="Digite a descrição do projeto"
              />
            </div>

            <div className="form-group">
              <label htmlFor="id_prazo">Prazos (Validação)</label>
              <select
                id="id_prazo"
                value={formData.id_prazo}
                onChange={(e) =>
                  setFormData({ ...formData, id_prazo: e.target.value })
                }
              >
                <option value="">Selecione um prazo de validação</option>
                {prazosValidacao
                  .filter((p) => p.ativo)
                  .map((prazo) => (
                    <option key={prazo.id} value={prazo.id}>
                      {prazo.descricao} ({prazo.validade} dias)
                    </option>
                  ))}
              </select>
            </div>

            <div className="form-group">
              <Toggle
                label="Status:"
                checked={formData.ativo}
                onChange={(e) => setFormData({ ...formData, ativo: e.target.checked })}
              />
            </div>

            <div className="form-actions">
              <button type="button" className="btn-secondary" onClick={resetForm}>
                <FaTimes /> Cancelar
              </button>
              <button type="submit" className="btn-primary">
                <FaSave /> {editingProjeto ? 'Atualizar' : 'Salvar'}
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="table-container">
            <div className="table-controls">
            <div className="search-container">
              <FaSearch />
              <input
                type="text"
                placeholder="Pesquisar projetos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="table-info">
              <span>{filteredProjetos.length} registro(s) encontrado(s)</span>
            </div>
            <button 
              className="btn-primary"
              onClick={() => setShowForm(true)}
            >
              <FaPlus /> Novo Projeto
            </button>
          </div>

          <div className="table-wrapper">
            <table className="data-table nucleos-table">
              <thead>
                <tr>
                  <th className="col-id">ID</th>
                  <th className="col-nome">Projeto</th>
                  <th className="col-nome" style={{ width: '200px', maxWidth: '200px' }}>Prazos</th>
                  <th className="col-status">Status</th>
                  <th className="col-acoes">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredProjetos.map(projeto => (
                  <tr key={projeto.id}>
                    <td className="col-id">{projeto.id}</td>
                    <td className="col-nome">{projeto.projeto}</td>
                    <td
                      className="col-nome"
                      style={{ width: '200px', maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                      title={getPrazoLabel(projeto)}
                    >
                      {getPrazoLabel(projeto)}
                    </td>
                    <td className="col-status">
                      <div className="status-toggle-wrapper">
                        <Toggle
                          id={`toggle-${projeto.id}`}
                          checked={projeto.ativo}
                          onChange={() => handleToggleStatus(projeto.id)}
                        />
                      </div>
                    </td>
                    <td className="col-acoes">
                      <div className="action-buttons">
                        <button 
                          className="btn-edit"
                          onClick={() => handleEdit(projeto)}
                          title="Editar"
                        >
                          <FaEdit />
                        </button>
                        <button 
                          className="btn-delete"
                          onClick={() => handleDelete(projeto.id)}
                          title="Excluir"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {filteredProjetos.length === 0 && (
              <div className="empty-state">
                <div className="empty-state-icon">
                  <FaSearch size={32} />
                </div>
                <h3>Nenhum projeto encontrado</h3>
                <p>Tente ajustar os filtros de pesquisa ou cadastre um novo projeto.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Projetos;
